// src/app/admin/optimizationPipeline/component/OptimizerDetailsSidebar.js
import React, { useMemo, useState } from 'react';
import Paper from '@material-ui/core/Paper';
import { grey } from '@material-ui/core/colors';
import {
    TableContainer,
    Table,
    TableHead,
    TableCell,
    TableRow,
    TableBody,
    Checkbox,
    withStyles,
    IconButton,
    Tooltip as MuiTooltip,
} from '@material-ui/core';
import { FaFilter } from 'react-icons/fa6';
import { MdBarChart } from 'react-icons/md';

import { useOptimizationPipeline } from '../OptimizationPipelineProvider';
import UnitOptimizerParamHistoryPopup from './UnitOptimizerParamHistoryPopup';

const DIFF_EPS = 1e-6;

const CustomCheckbox = withStyles({
    root: { color: grey[100], '&$checked': { color: grey[200] } },
    checked: {},
})((props) => <Checkbox color="default" {...props} />);

function isNumber(x) {
    return typeof x === 'number' && Number.isFinite(x);
}

function toMs(v) {
    if (!v) return NaN;
    if (v instanceof Date) return v.getTime();
    const t = new Date(v).getTime();
    return Number.isFinite(t) ? t : NaN;
}

function fmtNumber(v) {
    if (v == null) return '-';
    if (isNumber(v)) {
        const a = Math.abs(v);
        if (a >= 100) return v.toFixed(1);
        if (a >= 1) return v.toFixed(2);
        return v.toFixed(3);
    }
    if (typeof v === 'boolean') return v ? 'true' : 'false';
    return String(v);
}

function fmtPercent(v) {
    if (!isNumber(v)) return '-';
    return v.toFixed(2) + '%';
}

// Flattens nested objects to dot.notation keys (skips "id")
function flatten(obj, prefix = '', out = {}) {
    if (!obj || typeof obj !== 'object') return out;
    for (const [k, v] of Object.entries(obj)) {
        if (k === 'id') continue;
        const key = prefix ? prefix + '.' + k : k;
        if (v && typeof v === 'object' && !Array.isArray(v)) {
            flatten(v, key, out);
        } else {
            out[key] = v;
        }
    }
    return out;
}

function buildRowsForTs(list, ts) {
    const target = toMs(ts);
    if (!Array.isArray(list) || !Number.isFinite(target)) return [];

    // stabil összevetés (ne csak string egyezés)
    const selected = list.find((it) => Number.isFinite(toMs(it?.ts)) && toMs(it.ts) === target);
    if (!selected) return [];

    const base = flatten(selected.base_state || {});
    const best = flatten(selected.best_state || {});

    const keys = Array.from(new Set([...Object.keys(base), ...Object.keys(best)])).sort((a, b) =>
        a.localeCompare(b, 'hu')
    );

    return keys.map((name) => {
        const baseVal = base[name];
        const bestVal = best[name];
        const gain = isNumber(baseVal) && isNumber(bestVal) ? bestVal - baseVal : undefined;
        const pct =
            isNumber(baseVal) && baseVal !== 0 && isNumber(bestVal)
                ? (gain / Math.abs(baseVal)) * 100
                : undefined;

        return { name, base: baseVal, best: bestVal, gain, pct };
    });
}

function hasRealDifference(row) {
    const { base, best } = row;
    if (isNumber(base) && isNumber(best)) return Math.abs(best - base) > DIFF_EPS;
    if (base != null && best != null) return String(base) !== String(best);
    return false;
}

export default function OptimizerDetailsSidebar() {
    const { unitOptimizerLogDataList, ts } = useOptimizationPipeline();

    const [query, setQuery] = useState('');
    const [diffOnly, setDiffOnly] = useState(false);
    const [sortState, setSortState] = useState(null); // { key: 'name'|'base'|'best'|'gain'|'pct', dir: 'asc'|'desc' }

    const [popupOpen, setPopupOpen] = useState(false);
    const [popupSeedKeys, setPopupSeedKeys] = useState([]);

    const allRows = useMemo(
        () => buildRowsForTs(unitOptimizerLogDataList, ts),
        [unitOptimizerLogDataList, ts]
    );

    const filteredRows = useMemo(() => {
        let r = allRows;

        if (query.trim()) {
            const q = query.trim().toLowerCase();
            r = r.filter((row) => row.name.toLowerCase().includes(q));
        }

        if (diffOnly) {
            r = r.filter((row) => hasRealDifference(row));
        }

        return r;
    }, [allRows, query, diffOnly]);

    const sortedRows = useMemo(() => {
        if (!sortState) return filteredRows;
        const { key, dir } = sortState;
        const mult = dir === 'desc' ? -1 : 1;

        const getVal = (row) => row[key];

        const copy = [...filteredRows];
        copy.sort((a, b) => {
            const va = getVal(a);
            const vb = getVal(b);

            const aMissing = va == null;
            const bMissing = vb == null;
            if (aMissing && bMissing) return 0;
            if (aMissing) return -1 * mult;
            if (bMissing) return 1 * mult;

            if (isNumber(va) && isNumber(vb)) {
                const diff = va - vb;
                if (diff === 0) return 0;
                return diff > 0 ? 1 * mult : -1 * mult;
            }

            return String(va).localeCompare(String(vb), 'hu') * mult;
        });

        return copy;
    }, [filteredRows, sortState]);

    const hasData = allRows.length > 0;

    const toggleSort = (key) => {
        setSortState((prev) => {
            if (!prev || prev.key !== key) return { key, dir: 'asc' };
            if (prev.dir === 'asc') return { key, dir: 'desc' };
            return null; // tri-state: asc -> desc -> none
        });
    };

    const header = [
        { key: 'name', label: 'Attribute', align: 'left' },
        { key: 'base', label: 'Base State', align: 'right' },
        { key: 'best', label: 'Best State', align: 'right' },
        { key: 'gain', label: 'Change', align: 'right' },
        { key: 'pct', label: 'Change %', align: 'right' },
        { key: '__history', label: '', align: 'center' },
    ];

    const sortGlyph = (colKey) => {
        if (colKey === '__history') return '';
        if (sortState?.key !== colKey) return '↕';
        return sortState.dir === 'asc' ? '▲' : '▼';
    };

    const openTimeseries = (seedKeys) => {
        setPopupSeedKeys(Array.isArray(seedKeys) ? seedKeys : []);
        setPopupOpen(true);
    };

    // ✅ header gomb: alap kijelölés = csak diff-es kulcsok (aktuális TS alapján)
    const defaultDiffKeys = useMemo(() => {
        const keys = allRows
            .filter((r) => hasRealDifference(r))
            .map((r) => r.name)
            .filter(Boolean);

        // safety limit: ne jelöljön ki túl sokat
        const MAX_DEFAULT = 25;
        return keys.length > MAX_DEFAULT ? keys.slice(0, MAX_DEFAULT) : keys;
    }, [allRows]);

    return (
        <>
            <TableContainer component={Paper}>
                {/* Header bar styled like the reference filter component */}
                <div className="w-full flex itemscenter justify-between bg-black text-white p-6">
                    <div className="font-medium flex gap-12 items-center ml-12">
                        <FaFilter className="opacity-80" />
                        <span>Unit Optimizer</span>
                    </div>

                    <div className="flex items-center gap-12">
                        <MuiTooltip title="Open parameter timeseries" arrow>
                            <span>
                                <IconButton
                                    size="small"
                                    // ✅ itt jön a kérésed: diff-esek legyenek kijelölve
                                    onClick={() => openTimeseries(defaultDiffKeys)}
                                    style={{ color: 'white' }}
                                >
                                    <MdBarChart size={20} />
                                </IconButton>
                            </span>
                        </MuiTooltip>

                        <input
                            className="border border-white border-opacity-20 bg-transparent rounded px-6 py-3 text-xs outline-none focus:ring-2 focus:ring-white ring-opacity-20 placeholder-white"
                            placeholder="Search..."
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                        />

                        <div className="flex items-center gap-2">
                            <div className="text-xs opacity-50">Show only differences</div>
                            <CustomCheckbox checked={diffOnly} onChange={(e) => setDiffOnly(e.target.checked)} />
                        </div>
                    </div>
                </div>

                <Table className="w-full shadow-2xl">
                    <TableHead>
                        <TableRow>
                            {header.map((col) => (
                                <TableCell
                                    key={col.key}
                                    align={col.align}
                                    className="text-xs"
                                    onClick={() => col.key !== '__history' && toggleSort(col.key)}
                                    style={col.key !== '__history' ? { cursor: 'pointer', userSelect: 'none' } : undefined}
                                >
                                    <span className="text-xs font-semibold whitespace-nowrap inline-flex items-center gap-2">
                                        {col.label}
                                        {col.key !== '__history' && (<span className="opacity-60">{sortGlyph(col.key)}</span>)}
                                    </span>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {hasData ? (
                            sortedRows.length > 0 ? (
                                sortedRows.map((row) => {
                                    // ✅ háttérszínezés: gain alapján
                                    let bg;
                                    if (isNumber(row.gain)) {
                                        if (row.gain > DIFF_EPS) bg = '#ecfdf5'; // greenish
                                        else if (row.gain < -DIFF_EPS) bg = '#fef2f2'; // reddish
                                    }

                                    return (
                                        <TableRow key={row.name} style={bg ? { backgroundColor: bg } : undefined}>
                                            <TableCell align="left">{row.name}</TableCell>
                                            <TableCell align="right">{fmtNumber(row.base)}</TableCell>
                                            <TableCell align="right">{fmtNumber(row.best)}</TableCell>
                                            <TableCell align="right">{fmtNumber(row.gain)}</TableCell>
                                            <TableCell align="right">{fmtPercent(row.pct)}</TableCell>

                                            <TableCell align="center">
                                                <MuiTooltip title="Open timeseries (preselect this key)" arrow>
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                openTimeseries([row.name]);
                                                            }}
                                                        >
                                                            <MdBarChart size={18} />
                                                        </IconButton>
                                                    </span>
                                                </MuiTooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" style={{ opacity: 0.7, padding: 24 }}>
                                        No matches for current filters.
                                    </TableCell>
                                </TableRow>
                            )
                        ) : (
                            <TableRow>
                                <TableCell colSpan={6} align="center" style={{ opacity: 0.7, padding: 24 }}>
                                    No data for the selected timestamp.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <UnitOptimizerParamHistoryPopup
                open={popupOpen}
                onClose={() => setPopupOpen(false)}
                availableKeys={allRows.map(r => r.name)}
                defaultSelectedKeys={popupSeedKeys}
                initialMode="diff"
            />
        </>
    );
}
